import { normalize } from './dates';
import type { RoleIconKey } from './types';

/**
 * Ícones das funções, com rótulo, palavras de busca em português e uma cor própria
 * (uma das 24 de `MINISTRY_PALETTE`, que traz o par do tema escuro). Ao contrário do
 * ícone do ministério, que leva a cor do ministério, o da função tem a sua — como nos
 * aplicativos de escala que serviram de referência, cada papel se reconhece de longe.
 * O desenho de cada um (lucide-react) fica em `components/RoleIcon`.
 */
export interface RoleIconInfo {
  key: RoleIconKey;
  label: string;
  keywords: string;
  color: string;
}

const INDIGO = '#2563EB';
const GREEN = '#16A34A';
const CYAN = '#0891B2';
const PINK = '#DB2777';
const AMBER = '#D97706';
const VIOLET = '#7C3AED';
const BLUE = '#2563EB';
const SKY = '#0284C7';
const TEAL = '#0D9488';
const EMERALD = '#059669';
const ORANGE = '#EA580C';
const RED = '#DC2626';
const ROSE = '#E11D48';
const FUCHSIA = '#C026D3';
const PURPLE = '#9333EA';
const NAVY = '#1E40AF';
const BROWN = '#92400E';
const SLATE = '#475569';

export const ROLE_ICONS: readonly RoleIconInfo[] = [
  { key: 'user', label: 'Pessoa', keywords: 'membro voluntário geral função', color: SLATE },
  { key: 'users', label: 'Equipe', keywords: 'grupo time pessoas', color: INDIGO },
  {
    key: 'coordinator',
    label: 'Coordenação',
    keywords: 'coordenador líder responsável supervisor',
    color: NAVY,
  },
  { key: 'leader', label: 'Ministro', keywords: 'ministro louvor líder vocal ministração', color: VIOLET },
  { key: 'mic', label: 'Vocal', keywords: 'vocalista cantor canto voz microfone', color: PURPLE },
  { key: 'choir', label: 'Backing vocal', keywords: 'coral coro apoio vozes música', color: FUCHSIA },
  { key: 'guitar', label: 'Violão', keywords: 'violão guitarra instrumento cordas', color: ORANGE },
  { key: 'bass', label: 'Baixo', keywords: 'contrabaixo instrumento cordas', color: RED },
  { key: 'keyboard', label: 'Teclado', keywords: 'tecladista synth instrumento', color: SLATE },
  { key: 'piano', label: 'Piano', keywords: 'pianista instrumento', color: NAVY },
  { key: 'drums', label: 'Bateria', keywords: 'baterista ritmo instrumento', color: AMBER },
  { key: 'percussion', label: 'Percussão', keywords: 'percussionista tambor cajón ritmo', color: BROWN },
  { key: 'mixer', label: 'Mesa de som', keywords: 'som áudio operador mixagem sonoplastia', color: BLUE },
  { key: 'speaker', label: 'Som', keywords: 'caixa áudio sonoplastia retorno', color: SKY },
  { key: 'audio', label: 'Técnico de áudio', keywords: 'áudio som técnico mixagem', color: TEAL },
  { key: 'headset', label: 'Técnico live', keywords: 'fone transmissão técnico retorno', color: CYAN },
  { key: 'camera', label: 'Fotografia', keywords: 'fotógrafo câmera fotos', color: SLATE },
  {
    key: 'cameraOperator',
    label: 'Operador de câmera',
    keywords: 'câmera cinegrafista filmagem',
    color: ROSE,
  },
  { key: 'gimbal', label: 'Operador de gimbal', keywords: 'estabilizador câmera filmagem', color: SKY },
  { key: 'video', label: 'Videomaker', keywords: 'vídeo filmagem gravação', color: INDIGO },
  { key: 'director', label: 'Diretor de vídeo', keywords: 'direção corte switcher produção', color: RED },
  { key: 'live', label: 'Transmissão', keywords: 'live ao vivo online streaming youtube', color: RED },
  { key: 'projector', label: 'Projeção', keywords: 'projetor telão datashow', color: BLUE },
  { key: 'slides', label: 'Slides', keywords: 'letras apresentação holyrics telão', color: VIOLET },
  { key: 'lights', label: 'Iluminação', keywords: 'luz palco efeitos', color: AMBER },
  {
    key: 'mediaDesk',
    label: 'Coordenação de mídia',
    keywords: 'mídia monitor coordenação técnica',
    color: CYAN,
  },
  { key: 'videoEditor', label: 'Editor de vídeo', keywords: 'edição vídeo cortes montagem', color: BLUE },
  { key: 'photoEditor', label: 'Editor de foto', keywords: 'edição fotos tratamento imagem', color: SKY },
  { key: 'social', label: 'Social media', keywords: 'redes sociais instagram publicação', color: PINK },
  { key: 'story', label: 'Story', keywords: 'stories instagram registro', color: ROSE },
  { key: 'reels', label: 'Reels', keywords: 'vídeo curto celular instagram', color: FUCHSIA },
  { key: 'designer', label: 'Designer', keywords: 'design arte gráfica criação', color: ORANGE },
  { key: 'art', label: 'Criador de artes', keywords: 'artes pintura criação peças', color: EMERALD },
  { key: 'copy', label: 'Copywriter', keywords: 'texto legenda redação escrita', color: SLATE },
  { key: 'planning', label: 'Planejamento', keywords: 'conteúdo calendário cronograma pauta', color: INDIGO },
  { key: 'content', label: 'Conteúdo', keywords: 'redes publicação postagem envio', color: TEAL },
  { key: 'opening', label: 'Abertura do culto', keywords: 'abertura início sino liturgia', color: AMBER },
  { key: 'closing', label: 'Encerramento', keywords: 'encerramento final bênção liturgia', color: ORANGE },
  {
    key: 'welcome',
    label: 'Boas-vindas',
    keywords: 'boas vindas acolhimento visitantes recepção',
    color: BLUE,
  },
  { key: 'word', label: 'Palavra', keywords: 'bíblia mensagem pregação leitura', color: BROWN },
  { key: 'preaching', label: 'Preleção', keywords: 'púlpito pregação ministração ensino', color: ORANGE },
  { key: 'offering', label: 'Oferta', keywords: 'dízimos ofertas contribuição tesouraria', color: GREEN },
  { key: 'announcements', label: 'Anúncios', keywords: 'avisos comunicados recados', color: PINK },
  { key: 'monitor', label: 'Monitor de culto', keywords: 'roteiro checklist ordem do culto', color: TEAL },
  { key: 'prayer', label: 'Oração', keywords: 'oração inicial intercessão clamor', color: AMBER },
  { key: 'intercession', label: 'Intercessão', keywords: 'oração vigília clamor fogo', color: RED },
  { key: 'door', label: 'Portaria', keywords: 'porta entrada recepção', color: INDIGO },
  { key: 'guard', label: 'Segurança', keywords: 'guardiões proteção ordem', color: NAVY },
  { key: 'parking', label: 'Estacionamento', keywords: 'carros veículos manobra', color: SLATE },
  { key: 'support', label: 'Auxiliar', keywords: 'suporte geral apoio ajuda auxiliar', color: PURPLE },
  { key: 'cleaning', label: 'Limpeza', keywords: 'zeladoria faxina conservação', color: CYAN },
  {
    key: 'organization',
    label: 'Organização',
    keywords: 'organização checklist logística apoio',
    color: TEAL,
  },
  { key: 'teacher', label: 'Professor(a)', keywords: 'professor ensino aula turma tia', color: BLUE },
  {
    key: 'dynamics',
    label: 'Dinâmica',
    keywords: 'dinâmica brincadeiras atividades recreação',
    color: ORANGE,
  },
  { key: 'nursery', label: 'Berçário', keywords: 'bebês berçário maternal', color: PINK },
  { key: 'kids', label: 'Crianças', keywords: 'kids infantil criança turma', color: AMBER },
  { key: 'coffee', label: 'Cantina', keywords: 'café lanche hospitalidade', color: BROWN },
  { key: 'kitchen', label: 'Cozinha', keywords: 'refeição comida alimentação', color: ORANGE },
  { key: 'firstAid', label: 'Primeiros socorros', keywords: 'saúde enfermagem cuidado', color: RED },
  { key: 'communion', label: 'Santa ceia', keywords: 'ceia diaconato pão vinho', color: PURPLE },
  { key: 'baptism', label: 'Batismo', keywords: 'batismo água tanque', color: SKY },
  { key: 'decoration', label: 'Decoração', keywords: 'flores ornamentação ambiente', color: PINK },
  { key: 'interpreter', label: 'Intérprete', keywords: 'libras tradução intérprete idiomas', color: TEAL },
  { key: 'accessibility', label: 'Acessibilidade', keywords: 'inclusão cadeirante apoio', color: BLUE },
  { key: 'star', label: 'Destaque', keywords: 'especial estrela outros', color: AMBER },
];

const FALLBACK: RoleIconInfo = ROLE_ICONS[0]!;

export function roleIconInfo(key: RoleIconKey): RoleIconInfo {
  return ROLE_ICONS.find((icon) => icon.key === key) ?? FALLBACK;
}

/** Busca sem acento e sem caixa, no rótulo e nas palavras-chave. */
export function searchRoleIcons(query: string): readonly RoleIconInfo[] {
  const q = normalize(query.trim());

  if (!q) return ROLE_ICONS;

  return ROLE_ICONS.filter((icon) => normalize(`${icon.label} ${icon.keywords}`).includes(q));
}

/**
 * Ícone sugerido para o nome da função: o primeiro cujo rótulo ou palavra-chave aparece
 * no nome (sem acento e sem caixa), olhando primeiro os rótulos. Sem nada parecido,
 * a "Pessoa" genérica. Usado na função nova e nas funções que já existiam.
 */
export function suggestRoleIcon(name: string): RoleIconKey {
  const words = normalize(name)
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 3);

  if (!words.length) return 'user';

  const hit = (text: string) =>
    normalize(text)
      .split(/[^a-z0-9]+/)
      .some(
        (k) =>
          k.length >= 3 &&
          words.some(
            (w) => w === k || (Math.min(w.length, k.length) >= 5 && (w.startsWith(k) || k.startsWith(w)))
          )
      );

  return (
    ROLE_ICONS.find((icon) => icon.key !== 'user' && hit(icon.label))?.key ??
    ROLE_ICONS.find((icon) => icon.key !== 'user' && hit(icon.keywords))?.key ??
    'user'
  );
}
